import React from 'react';

/**
 * Model Documentation Modal - Shows detailed data model documentation
 * @param {boolean} show - Modal visibility
 * @param {function} onHide - Close handler
 * @param {string} modelType - Type of model (ankr_user|healthcare_user|patient|sales_user|organization)
 */
const ModelDocumentationModal = ({ show, onHide, modelType }) => {
  const docs = {
    ankr_user: {
      title: 'Ankr Users',
      description: 'Internal Ankr employees who manage and operate the platform.',
      fields: [
        { name: 'user', type: 'OneToOne(Django User)', desc: 'Link to Django auth user' },
        { name: 'role', type: 'ForeignKey(Role)', desc: 'User role (Admin, Support, etc.)' },
        { name: 'department', type: 'String', desc: 'Department (Engineering, Operations, etc.)' },
        { name: 'manager', type: 'ForeignKey(AnkrUser)', desc: 'Direct manager' },
        { name: 'employee_id', type: 'String', desc: 'Internal employee ID' }
      ],
      relationships: [
        'Reports to Manager (self-referential)',
        'Has Direct Reports',
        'Belongs to Department',
        'Has Role with Permissions'
      ],
      examples: [
        'System administrators managing health systems',
        'Support staff assisting healthcare providers',
        'Engineers maintaining the platform'
      ]
    },
    healthcare_user: {
      title: 'Healthcare Users',
      description: 'Doctors, nurses, and clinical staff who provide patient care.',
      fields: [
        { name: 'user', type: 'OneToOne(Django User)', desc: 'Link to Django auth user' },
        { name: 'role', type: 'ForeignKey(Role)', desc: 'Clinical role (Doctor, Nurse, etc.)' },
        { name: 'npi', type: 'String', desc: 'National Provider Identifier' },
        { name: 'primary_specialty', type: 'ForeignKey(Specialty)', desc: 'Primary medical specialty' },
        { name: 'manager', type: 'ForeignKey(HealthcareUser)', desc: 'Clinical supervisor' }
      ],
      relationships: [
        'Multiple Health System Memberships (via UserHealthSystemMembership)',
        'Multiple EHR Profiles per membership (Epic, Cerner, etc.)',
        'Multiple SSO Configs per membership (Microsoft, Google, Okta)',
        'Treats Patients',
        'Reports to Clinical Supervisor'
      ],
      examples: [
        'Doctor with Epic EHR at Hospital A and Cerner at Hospital B',
        'Nurse using Microsoft SSO and Epic MyChart SSO',
        'Provider with access to multiple departments'
      ]
    },
    patient: {
      title: 'Patients',
      description: 'Individuals receiving healthcare services with optional portal access.',
      fields: [
        { name: 'user', type: 'OneToOne(Django User)', desc: 'Portal login (optional)' },
        { name: 'health_system', type: 'ForeignKey(HealthSystem)', desc: 'Primary health system' },
        { name: 'date_of_birth', type: 'Date', desc: 'Date of birth' },
        { name: 'gender', type: 'String', desc: 'Gender identity' }
      ],
      relationships: [
        'Multiple Health System Memberships',
        'Multiple EHR Profiles (different depts, migrated records)',
        'Multiple SSO Configs (MyChart, Google, Microsoft)',
        'Has Cases (medical visits)',
        'Has Forms (intake, consent)',
        'Assigned to Providers'
      ],
      examples: [
        'Patient with portal access at 2 hospitals',
        'Patient with Epic EHR and legacy Cerner records',
        'Patient using Google SSO and MyChart SSO'
      ]
    },
    sales_user: {
      title: 'Sales Users',
      description: 'SDRs, AEs, and sales managers from organizations using the sales platform.',
      fields: [
        { name: 'user', type: 'OneToOne(Django User)', desc: 'Link to Django auth user' },
        { name: 'role', type: 'ForeignKey(Role)', desc: 'Sales role (SDR, AE, Manager)' },
        { name: 'territory', type: 'String', desc: 'Sales territory' },
        { name: 'sales_team', type: 'ForeignKey(SalesTeam)', desc: 'Primary team' },
        { name: 'manager', type: 'ForeignKey(SalesUser)', desc: 'Sales manager' },
        { name: 'quota', type: 'Decimal', desc: 'Sales quota' }
      ],
      relationships: [
        'Multiple Organization Memberships (via OrganizationMembership)',
        'Belongs to Sales Team',
        'Reports to Manager',
        'Owns Leads and Opportunities',
        'Covers Territory'
      ],
      examples: [
        'SDR working for multiple client organizations',
        'AE with access to parent and child orgs',
        'Manager overseeing regional team'
      ]
    },
    organization: {
      title: 'Organizations',
      description: 'Companies using the sales platform, with hierarchical structure support.',
      fields: [
        { name: 'name', type: 'String', desc: 'Organization name' },
        { name: 'parent', type: 'ForeignKey(Organization)', desc: 'Parent organization' },
        { name: 'type', type: 'String', desc: 'Org type (enterprise, SMB, etc.)' },
        { name: 'industry', type: 'String', desc: 'Industry vertical' }
      ],
      relationships: [
        'Parent-Child Hierarchy (e.g., Corp → Divisions)',
        'Has Sales Teams',
        'Has Organization Members (Sales Users)',
        'Has Prospects/Opportunities',
        'Has Phone Numbers'
      ],
      examples: [
        'Enterprise with multiple regional divisions',
        'SMB with single location',
        'Holding company with subsidiary orgs'
      ]
    }
  };

  const doc = docs[modelType] || docs.ankr_user;

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onHide}>
      <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title"><i className="fas fa-book me-2" />{doc.title} Data Model</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} />
          </div>
          <div className="modal-body">
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2" />
              <strong>About:</strong> {doc.description}
            </div>

            <h6 className="text-primary mb-2"><i className="fas fa-table me-2" />Key Fields</h6>
            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.fields.map((field, idx) => (
                    <tr key={idx}>
                      <td className="font-monospace small">{field.name}</td>
                      <td><span className="badge bg-secondary small">{field.type}</span></td>
                      <td className="small">{field.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h6 className="text-primary mb-2"><i className="fas fa-project-diagram me-2" />Relationships</h6>
            <ul className="list-unstyled small">
              {doc.relationships.map((rel, idx) => (
                <li key={idx} className="mb-1">
                  <i className="fas fa-arrow-right text-muted me-2" />
                  {rel}
                </li>
              ))}
            </ul>

            <h6 className="text-primary mb-2 mt-3"><i className="fas fa-lightbulb me-2" />Example Use Cases</h6>
            <ul className="small">
              {doc.examples.map((ex, idx) => (
                <li key={idx} className="mb-1">{ex}</li>
              ))}
            </ul>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onHide}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelDocumentationModal;
